import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  FaUsers, FaUserTie, FaUserCircle, FaGraduationCap, FaCalendarAlt,
  FaBook, FaCog, FaComments, FaSignOutAlt, FaUserCog,
} from 'react-icons/fa';
import NotificationBell from '../NotificationBell';
import { FoundingMemberBadge } from '../FoundingMemberBadge';
import { GatedLink, FEATURES } from '../FeatureGate';
import { useAdminNotifications } from '../../context/AdminNotificationContext';
import { SubscriptionBadge } from './SubscriptionBadge';
import { ChangePasswordButton } from './ChangePasswordButton';
import { DeleteAccountButton } from './DeleteAccountButton';
import { MobileNavMenu } from './MobileNavMenu';

const API = '';

/**
 * Dashboard-specific navigation items
 */
function AccountActions({ handleLogout }) {
  return (
    <>
      <ChangePasswordButton />
      <DeleteAccountButton />
      <button
        onClick={handleLogout}
        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-xs sm:text-sm"
        data-testid="logout-btn"
        title="Logout"
      >
        <FaSignOutAlt />
        <span>Logout</span>
      </button>
    </>
  );
}

export function AdminButton() {
  const { unreadCount: adminNotifCount } = useAdminNotifications();
  return (
    <Link
      to="/admin/professionals"
      className="relative flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition text-sm"
      data-testid="admin-btn"
      title={`Admin Panel ${adminNotifCount > 0 ? `(${adminNotifCount} new)` : ''}`}
    >
      <FaCog className="text-lg sm:text-xl" />
      <span>Admin</span>
      {adminNotifCount > 0 && (
        <span
          className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
          data-testid="admin-notification-badge"
        >
          {adminNotifCount > 9 ? '9+' : adminNotifCount}
        </span>
      )}
    </Link>
  );
}

export function DashboardNavItems({ user, handleLogout, groupAccount = false, hideAdmin = false }) {
  const [hasProfessionalProfile, setHasProfessionalProfile] = useState(false);

  // Check if user has a professional profile
  useEffect(() => {
    const checkProfessionalProfile = async () => {
      try {
        const sessionToken = localStorage.getItem('session_token');
        if (!sessionToken) return;

        const response = await axios.get(`${API}/api/directory/my-profile`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });

        // If we get a response, user has a professional profile
        if (response.data && response.data.status === 'approved') {
          setHasProfessionalProfile(true);
        }
      } catch (err) {
        // 404 means no profile, which is fine
        setHasProfessionalProfile(false);
      }
    };

    checkProfessionalProfile();
  }, []);

  return (
    <>
      {/* Notifications */}
      <NotificationBell />

      {/* Professional Dashboard Button - only show if user has approved professional profile */}
      {hasProfessionalProfile && (
        <Link
          to="/professionals/dashboard"
          className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition text-xs sm:text-sm"
          data-testid="professional-dashboard-btn"
          title="Professional Dashboard"
        >
          <FaUserTie className="text-lg sm:text-xl" />
          <span>Pro Dashboard</span>
        </Link>
      )}

      {/* Resources Button */}
      <GatedLink
        feature={FEATURES.RESOURCES_PROFESSIONALS}
        to="/resources"
        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition text-xs sm:text-sm"
        data-testid="resources-btn"
        title="Resources"
      >
        <FaGraduationCap className="text-lg sm:text-xl" />
        <span>Resources</span>
      </GatedLink>

      {/* Consultation Schedule Button */}
      <GatedLink
        feature={FEATURES.RESOURCES_PROFESSIONALS}
        to="/consultation-schedule"
        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition text-xs sm:text-sm"
        data-testid="consultation-schedule-btn"
        title="Consultations"
      >
        <FaCalendarAlt className="text-lg sm:text-xl" />
        <span>Consultations</span>
      </GatedLink>

      {/* Role Guide Button */}
      <Link
        to="/role-guide"
        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg transition text-xs sm:text-sm"
        data-testid="role-guide-btn"
        title="Family Role Guide"
      >
        <FaBook className="text-lg sm:text-xl" />
        <span className="whitespace-nowrap">Role Guide</span>
      </Link>

      {user?.is_platform_admin && !hideAdmin && <AdminButton />}

      {/* My Invitations Button */}
      <Link
        to="/my-invitations"
        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition text-xs sm:text-sm"
        data-testid="my-invitations-btn"
        title="Professional Network & Invitations"
      >
        <FaUsers className="text-lg sm:text-xl" />
        <span className="whitespace-nowrap">Pro Invites</span>
      </Link>

      {/* Messages Button */}
      <Link
        to="/messages"
        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition text-xs sm:text-sm"
        data-testid="messages-btn"
        title="Messages"
      >
        <FaComments className="text-lg sm:text-xl" />
        <span>Messages</span>
      </Link>

      {/* User Profile with Subscription Badge */}
      <Link
        to="/subscription"
        className="flex items-center space-x-2 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors"
        data-testid="user-profile"
      >
        {user?.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            className="w-10 h-10 rounded-full"
          />
        ) : (
          <FaUserCircle className="text-gray-600 text-3xl" />
        )}
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5">
            <span className="text-gray-700 font-medium whitespace-nowrap">{user?.name}</span>
            <FoundingMemberBadge variant="icon" />
          </div>
          <SubscriptionBadge />
        </div>
      </Link>

      {groupAccount ? (
        <MobileNavMenu label="Account" title="Account" testId="account-menu-btn" icon={<FaUserCog className="text-lg" />}>
          <AccountActions handleLogout={handleLogout} />
        </MobileNavMenu>
      ) : (
        <AccountActions handleLogout={handleLogout} />
      )}
    </>
  );
}
